import {useAppTranslation} from "services/i18n";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {Formik} from "formik";
import {OptionValue} from "model/form";
import {Dialog, DialogActions, DialogContent, Grid, Paper} from "@mui/material";
import Button from "@mui/material/Button";
import {SelectFormField} from "components/form/SelectFormField";
import {DataGridColFilter} from "components/DataGrid";
import {defaultDialogStyle} from "utils/dialog";
import {UserGroupOrderGridFilter} from "components/userGroup/UserGroupRegisteredTab";

export type UserGroupOrderFilterType =
    'paymentMethod'
    ;

const dialogStyle = defaultDialogStyle(600);

export const UserGroupOrderFilterDialog = ({colFilter, onSuccess, onClose}: {
    colFilter: DataGridColFilter<UserGroupOrderGridFilter, UserGroupOrderFilterType>,
    onSuccess?: (result: any) => void,
    onClose: () => void
}) => {
    const t = useAppTranslation();

    const [isSaving, setIsSaving] = useState(false);

    const filterName = colFilter.filterName as keyof typeof colFilter.currentFilter;

    const initialValues: UserGroupOrderGridFilter = useMemo(() => {
        return {
            [filterName]: colFilter.currentFilter[filterName]
        }
    }, [colFilter.currentFilter, filterName]);

    const handleValidate = useCallback((values: UserGroupOrderGridFilter) => {
        // let errors = {} as FormikErrors<UserGroupOrderGridFilter>;
        //
        // return errors;
        return {};
    }, []);

    const handleSave = useCallback(async (values: UserGroupOrderGridFilter) => {
        setIsSaving(true);
        const result = {[colFilter.filterName]: undefined, ...values}; // undefined needed to override any previous value
        colFilter.onApply(result);
        if (onSuccess) {
            onSuccess(result);
        }
        onClose();
        setIsSaving(false);
    }, [colFilter, onSuccess, onClose]);

    const handleReset = useCallback(async () => {
        const defaultValue = colFilter.defaultFilter[filterName];
        await handleSave({
            [filterName]: defaultValue === undefined
                ? defaultValue
                : JSON.parse(JSON.stringify(defaultValue))
        });
    }, [filterName, colFilter.defaultFilter, handleSave]);

    const isFilterUsed = useMemo(() => {
        return colFilter.currentFilter[filterName] !== colFilter.defaultFilter[filterName]
            && JSON.stringify(colFilter.currentFilter) !== JSON.stringify(colFilter.defaultFilter[filterName]);
    }, [filterName, colFilter.currentFilter, colFilter.defaultFilter]);

    const paymentMethodOptions: OptionValue[] = useMemo(() => [
        {value: 'bank_transfer', label: t('userGroup.paymentMethod.bank_transfer')},
        {value: 'braintree', label: t('userGroup.paymentMethod.braintree')},
        {value: 'code', label: t('userGroup.paymentMethod.code')},
    ], [t]);

    return <Dialog open={true} onClose={onClose} PaperComponent={Paper} sx={dialogStyle}>
        <Formik
            initialValues={initialValues}
            onSubmit={handleSave}
            validate={handleValidate}
        >
            {({handleSubmit, setFieldValue, values}) => {
                return <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <Grid container spacing={1} sx={{
                            minHeight: '100px',
                            'small': {color: 'var(--color-black-text)'}
                        }} className={'tw-mt-8 tw-pt-8'}>
                            <Grid size={12} sx={{position: 'relative'}}>
                                {filterName === 'paymentMethod'
                                    && <SelectFormField name={filterName} options={paymentMethodOptions} label={t('userGroup.registration.paymentMethod')} onChange={handleSubmit}/>}
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{padding: '16px'}}>
                        <Button color={'inherit'} onClick={onClose} disabled={isSaving}>
                            {t('form.cancel')}
                        </Button>
                        {isFilterUsed && <Button color={'secondary'} onClick={handleReset} disabled={isSaving}>
                            {t('userGroup.filter.cancel')}
                        </Button>}
                        <Button color={'primary'} variant={'contained'} type={'submit'} disabled={isSaving}>
                            {t('userGroup.filter.apply')}
                        </Button>
                    </DialogActions>
                </form>
            }}
        </Formik>
    </Dialog>;
};
